import React, { useState } from 'react';
import type { ToolUseBlock, ToolResultBlock } from '../../store/chatStore';
import { useIsPleriDebugMode } from '../ChatArea/MessageActions';
import { safeJSONParse } from '../tool-results/renderers/Table/utils';

import { useTranslation } from 'react-i18next';

interface DebugViewProps {
  toolUse: ToolUseBlock;
  result?: ToolResultBlock;
}

type StatusType = 'success' | 'error' | 'pending';

const StatusBadge: React.FC<{ status: StatusType }> = ({ status }) => {
  const { t } = useTranslation('pleri/chat');
  if (status === 'success') {
    return (
      <span className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950/40 dark:text-green-400">
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
        OK
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/40 dark:text-red-400">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
        {t('error')}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      {t('pending')}
    </span>
  );
};

const CopyInlineButton: React.FC<{ textToCopy: string }> = ({ textToCopy }) => {
  const { t } = useTranslation('pleri/chat');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={handleCopy}
      className="text-xs text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
    >
      {copied ? t('copied') : t('copy')}
    </button>
  );
};

export const DebugView: React.FC<DebugViewProps> = ({ toolUse, result }) => {
  const { t } = useTranslation('pleri/chat');
  const [showRawData, setShowRawData] = useState(false);
  const isDebugMode = useIsPleriDebugMode();
  const isSuccess = !result?.isError;

  const resultStatus: StatusType = result
    ? isSuccess
      ? 'success'
      : 'error'
    : 'pending';

  const getParsedToolUseData = (): string => {
    if (showRawData) {
      return JSON.stringify(toolUse, null, 2);
    }

    const { name, input } = toolUse;
    if (!input || typeof input !== 'object') {
      return JSON.stringify({ name, input }, null, 2);
    }

    const parsedInput = Object.fromEntries(
      Object.entries(input).map(([key, value]) => [
        key,
        typeof value === 'string' ? safeJSONParse(value) : value,
      ]),
    );

    return JSON.stringify(
      {
        name,
        input: parsedInput,
      },
      null,
      2,
    );
  };

  const getParsedResultData = (): string => {
    if (!result) {
      return t('waitingForResult');
    }
    if (showRawData) {
      return JSON.stringify(result, null, 2);
    }

    try {
      const parsedContent =
        typeof result.content === 'string'
          ? JSON.parse(result.content)
          : result.content;
      return JSON.stringify(parsedContent, null, 2);
    } catch {
      return String(result.content);
    }
  };

  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Request section */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500">
              {t('request')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isDebugMode && (
              <button
                onClick={() => setShowRawData(!showRawData)}
                className="text-xs text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showRawData ? t('viewParsed') : t('viewRaw')}
              </button>
            )}
            <CopyInlineButton textToCopy={JSON.stringify(toolUse, null, 2)} />
          </div>
        </div>
        <pre className="overflow-x-auto rounded-lg bg-[#0d1117] p-4 font-mono text-xs leading-relaxed text-[#e6edf3]">
          {getParsedToolUseData()}
        </pre>
      </div>
      {/* Response section */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500">
              {t('response')}
            </span>
            <StatusBadge status={resultStatus} />
          </div>
          <div className="flex items-center gap-2">
            {result && (
              <CopyInlineButton textToCopy={JSON.stringify(result, null, 2)} />
            )}
          </div>
        </div>
        <pre className="overflow-x-auto rounded-lg bg-[#0d1117] p-4 font-mono text-xs leading-relaxed text-[#e6edf3]">
          {getParsedResultData()}
        </pre>
      </div>
    </div>
  );
};
